import { useCallback, useEffect, useState } from 'react'
import {
  APP_VERSION,
  CHANGELOG,
  LAST_SEEN_VERSION_KEY,
  OPEN_CHANGELOG_EVENT,
  compareVersions,
  type ChangelogEntry,
  type ChangelogText,
} from '@/changelog'
import { useLanguage } from '@/i18n/LanguageProvider'
import { useT } from '@/i18n/useT'
import { Button, CenterModal, ModalHeader } from './ui'

function readLastSeen(): string | null {
  try {
    return window.localStorage.getItem(LAST_SEEN_VERSION_KEY)
  } catch {
    return null // private mode / storage disabled
  }
}

function writeLastSeen(version: string): void {
  try {
    window.localStorage.setItem(LAST_SEEN_VERSION_KEY, version)
  } catch {
    // ignore
  }
}

/**
 * "What's new" overview of the latest release(s), shown once after an update and
 * re-openable any time via OPEN_CHANGELOG_EVENT (the version badge in the header
 * dispatches it). Auto-open lists only unseen versions, manual open lists all.
 *
 * Changelog prose carries both languages inline (see src/changelog.ts), so it is
 * picked here rather than translated through t(); only the chrome uses t().
 */
export default function WhatsNewDialog() {
  const { t } = useT()
  const { lang } = useLanguage()
  const [open, setOpen] = useState(false)
  const [showAll, setShowAll] = useState(false)

  const pick = (text: ChangelogText) => (lang === 'de' ? text.de : text.en)

  // Auto-open once per new version. A first-time user has no marker at all and
  // would get the whole history dumped on them, so that case is marked as seen
  // without opening — the badge is there when they want it.
  useEffect(() => {
    const lastSeen = readLastSeen()
    if (lastSeen === APP_VERSION) return
    if (lastSeen == null) {
      writeLastSeen(APP_VERSION)
      return
    }
    setShowAll(false)
    setOpen(true)
  }, [])

  // Manual open from anywhere (header badge).
  useEffect(() => {
    const onOpen = () => {
      setShowAll(true)
      setOpen(true)
    }
    window.addEventListener(OPEN_CHANGELOG_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_CHANGELOG_EVENT, onOpen)
  }, [])

  const close = useCallback(() => {
    writeLastSeen(APP_VERSION)
    setOpen(false)
  }, [])

  const lastSeen = readLastSeen()
  const unseen: ChangelogEntry[] =
    lastSeen == null
      ? CHANGELOG
      : CHANGELOG.filter((entry) => compareVersions(entry.version, lastSeen) > 0)
  const entries = showAll || unseen.length === 0 ? CHANGELOG : unseen

  if (!open) return null

  return (
    <CenterModal
      open={open}
      onClose={close}
      width={660}
      header={
        <ModalHeader
          title={t("What's new")}
          subtitle={
            showAll
              ? `${t('All versions')} · ${CHANGELOG.length}`
              : `${t('Since your last visit')} · v${APP_VERSION}`
          }
          onClose={close}
        />
      }
      footer={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 18px',
            borderTop: '1px solid var(--line)',
          }}
        >
          {!showAll && (
            <Button variant="ghost" onClick={() => setShowAll(true)}>
              {t('Show all versions')}
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <Button onClick={close}>{t('Got it')}</Button>
        </div>
      }
    >
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 20 }}>
        {entries.map((entry) => (
          <div key={entry.version}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  border: '1px solid var(--line)',
                  borderRadius: 999,
                  padding: '2px 8px',
                  color: 'var(--muted)',
                  whiteSpace: 'nowrap',
                }}
              >
                v{entry.version}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, flex: 1, minWidth: 0 }}>
                {pick(entry.title)}
              </div>
              <div style={{ fontSize: 12, color: 'var(--faint)' }}>
                {new Date(entry.date).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-GB', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })}
              </div>
            </div>
            <ul
              style={{
                margin: '8px 0 0',
                paddingLeft: 18,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              {entry.changes.map((change, i) => (
                <li key={i} style={{ fontSize: 13, lineHeight: 1.5 }}>
                  {pick(change)}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </CenterModal>
  )
}
